import { useState, ReactElement, MouseEvent } from 'react';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { translateString } from 'api';
import { setSubscriptionError } from 'reducers/pageAction';
import { renderSnackbar } from 'utils';
import { useOriginalString, OriginalString } from './useOriginalString';
import {
  ApiResponse,
  ErrorPayload,
} from 'interface/common/ApiResponseInterface';
import { HazardFormField, SDSInfo } from 'interface';
import { TranslateDto } from 'interface/sds-detail';
import { AppDispatch } from 'const';
/* Component */
import {
  Button,
  CircularProgress,
  ClassNameMap,
  InputAdornment,
} from '@mui/material';
import { GTranslate } from '@mui/icons-material';

export type TranslateField = keyof SDSInfo | keyof HazardFormField | string;

type ReturnType = {
  renderAdormentIcon: () => ReactElement | null;
  renderBtn: () => ReactElement | null;
  loading: boolean;
  inputValue: OriginalString;
};

export const useTranslate = (
  inputLang?: string | null,
  translateLang?: string | null,
  inputString?: string | null,
  componentId?: number | string | null,
  field?: keyof SDSInfo | keyof HazardFormField | string | null,
  fields?: Array<TranslateField> | null,
  onTranslateSuccess?: (translatedString: OriginalString) => void
): ReturnType => {
  const { t } = useTranslation(['sds_authoring']);
  const classes: ClassNameMap = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [translated, setTranslated] = useState<boolean>(false);
  const { originalString } = useOriginalString(field, fields, componentId);
  const inputValue = inputString ?? originalString;
  const needTranslated =
    !!inputValue &&
    !!inputLang &&
    !!translateLang &&
    inputLang !== translateLang;

  const onTranslate = (e: MouseEvent): void => {
    if (!needTranslated || !inputValue || !inputLang || !translateLang) return;

    const translateMultiple = typeof inputValue === 'object';

    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    translateString({
      ...(translateMultiple
        ? { input_data: inputValue }
        : { input_string: inputValue }),
      input_language: inputLang,
      translate_language: translateLang,
    })
      .then((response: ApiResponse<TranslateDto | ErrorPayload>) => {
        if (response.status === 200) {
          const responseData = response.data as TranslateDto;
          onTranslateSuccess !== undefined &&
            onTranslateSuccess(
              responseData.translated_data ??
                responseData.translated_string ??
                null
            );
          setTranslated(true);
        } else if (response.status === 500) {
          renderSnackbar([t('account:some_thing_wrong')]);
        } else {
          dispatch(
            setSubscriptionError((response.data as ErrorPayload).error_code)
          );
        }
        setLoading(false);
      })
      .catch(error => {
        renderSnackbar([t('account:some_thing_wrong')]);
      });
  };

  const renderAdormentIcon = (): ReactElement | null => {
    if (loading) {
      return (
        <InputAdornment position="end">
          <CircularProgress color="primary" size={20} />
        </InputAdornment>
      );
    }

    if (needTranslated && !translated && !loading) {
      return (
        <InputAdornment position="end">
          <GTranslate className={classes.translateIcon} onClick={onTranslate} />
        </InputAdornment>
      );
    }

    return null;
  };

  const renderBtn = (): ReactElement | null => {
    if (needTranslated && !translated) {
      return (
        <Button
          className={classes.translatedBtn}
          variant="contained"
          onClick={onTranslate}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress color="primary" size={16} />
          ) : (
            <GTranslate
              className={classes.translateIcon}
              onClick={onTranslate}
            />
          )}
          {t('sds_authoring:translate_all_strings_in_section')}
        </Button>
      );
    }

    return null;
  };

  return { renderAdormentIcon, renderBtn, loading, inputValue };
};
